// The signed-in user's own account security: two-step verification status,
// moving to a new device, and regenerating recovery codes.
//
// Both sensitive actions re-ask for the password: holding an open session
// should not be enough to swap out somebody's second factor.

import { useState, type FormEvent } from 'react';
import { api, type EnrolmentOffer } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useToast } from '../components/Toast';
import { Icon } from '../components/Icon';
import { RecoveryCodes, TwoFactorSetup } from '../components/TwoFactorSetup';
import { ErrorAlert, LoadingRow, Modal, TextField } from '../components/ui';
import { formatDate } from '@shared/format';

type Dialog =
  | { name: 'reconfigure-password' }
  | { name: 'reconfigure'; offer: EnrolmentOffer }
  | { name: 'regenerate-password' }
  | { name: 'codes'; codes: string[] };

export function Security() {
  const t = useT();
  const { locale } = useI18n();
  const { user } = useSession();
  const toast = useToast();

  const [dialog, setDialog] = useState<Dialog>();
  const status = useAsync(() => api.securityStatus(), []);

  if (status.loading && !status.data) return <LoadingRow />;
  if (status.error) return <ErrorAlert message={status.error} onRetry={status.reload} />;

  const remaining = status.data?.recoveryCodesRemaining ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">{t('twofactor.myAccount')}</h1>
          <p className="page-head__subtitle">
            {user ? `${user.firstName} ${user.lastName} · ${user.email}` : ''}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">{t('twofactor.title')}</h2></div>
        <div className="card__body stack">
          <div className="row row--between">
            <div className="row">
              <span className="badge badge--paid">
                <Icon name="shield" size={13} />
                {t('common.active')}
              </span>
              {status.data?.confirmedAt && (
                <span className="muted">
                  {t('twofactor.enabledSince', { date: formatDate(status.data.confirmedAt, locale) })}
                </span>
              )}
            </div>
          </div>

          <p className="muted">{t('twofactor.required')}</p>

          <div className={remaining <= 3 ? 'alert alert--warning' : 'alert alert--info'}>
            <Icon name={remaining <= 3 ? 'alert-circle' : 'check-circle'} />
            <div style={{ flex: 1 }}>
              {remaining <= 3
                ? t('twofactor.recoveryLow', { count: remaining })
                : t('twofactor.recoveryRemaining', { count: remaining })}
            </div>
          </div>

          <div className="row">
            <button type="button" className="btn"
              onClick={() => setDialog({ name: 'reconfigure-password' })}>
              <Icon name="shield" />{t('twofactor.reconfigure')}
            </button>
            <button type="button" className="btn"
              onClick={() => setDialog({ name: 'regenerate-password' })}>
              <Icon name="key" />{t('twofactor.regenerate')}
            </button>
          </div>
          <p className="field__hint">{t('twofactor.reconfigureHint')}</p>
        </div>
      </div>

      {dialog?.name === 'reconfigure-password' && (
        <PasswordPrompt
          title={t('twofactor.reconfigure')}
          onCancel={() => setDialog(undefined)}
          onConfirm={async (password) => {
            setDialog({ name: 'reconfigure', offer: await api.reconfigureTotp(password) });
          }}
        />
      )}

      {dialog?.name === 'reconfigure' && (
        <Modal title={t('twofactor.reconfigure')} onClose={() => setDialog(undefined)}>
          <TwoFactorSetup
            offer={dialog.offer}
            onConfirm={async (code) => {
              const result = await api.confirmReconfigure(code);
              setDialog({ name: 'codes', codes: result.recoveryCodes });
              status.reload();
            }}
          />
        </Modal>
      )}

      {dialog?.name === 'regenerate-password' && (
        <PasswordPrompt
          title={t('twofactor.regenerate')}
          onCancel={() => setDialog(undefined)}
          onConfirm={async (password) => {
            const result = await api.regenerateRecoveryCodes(password);
            setDialog({ name: 'codes', codes: result.recoveryCodes });
            toast.success(t('twofactor.regenerated'));
            status.reload();
          }}
        />
      )}

      {dialog?.name === 'codes' && (
        <Modal title={t('twofactor.recoveryTitle')} onClose={() => setDialog(undefined)}>
          <RecoveryCodes codes={dialog.codes} onAcknowledge={() => setDialog(undefined)} />
        </Modal>
      )}
    </>
  );
}

/** Re-asks for the password before a change to the second factor. */
export function PasswordPrompt({ title, hint, onConfirm, onCancel }: {
  title: string;
  hint?: string;
  onConfirm: (password: string) => Promise<void>;
  onCancel: () => void;
}) {
  const t = useT();
  const toMessage = useErrorMessage();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onConfirm(password);
    } catch (err) {
      setError(fieldErrors(err).password ?? toMessage(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="password-prompt" className="btn btn--primary" disabled={busy}>
            {busy && <span className="spinner" />}
            {t('common.save')}
          </button>
        </>
      }
    >
      <form id="password-prompt" onSubmit={onSubmit} noValidate className="stack">
        {error && <ErrorAlert message={error} />}
        {hint && <p className="muted">{hint}</p>}
        <TextField
          label={t('twofactor.confirmPassword')}
          type="password"
          autoComplete="current-password"
          autoFocus
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </form>
    </Modal>
  );
}
